<script setup lang="ts">
import type { ClipDisplayItem } from '~/composables/useCharacterPage'
import { DEFAULT_SECTION_TITLES, type TextSettings, type ThemeSettings, type SectionSettings } from '~/stores/auth'

const props = defineProps<{
  clips: ClipDisplayItem[]
  themeSettings: ThemeSettings
  sectionSettings: SectionSettings
  textSettings: TextSettings
  characterId?: string
  editMode?: boolean
}>()

const emit = defineEmits<{
  'open-clips-section': []
  'upload-clip': []
}>()

const overlay = useOverlay()

const activeIndex = ref(0)

// Carousel items with basis class for multi-item view
const carouselItems = computed(() =>
  props.clips.map(clip => ({ ...clip, class: 'basis-full sm:basis-1/2 md:basis-1/3' }))
)

// Page counter: show which "page" of 3 we're on
const currentPage = computed(() => Math.floor(activeIndex.value / 3) + 1)
const totalPages = computed(() => Math.ceil(props.clips.length / 3))

function onClickPrev() {
  activeIndex.value--
}

function onClickNext() {
  activeIndex.value++
}

function onSelect(index: number) {
  activeIndex.value = index
}

function asClip(item: unknown): ClipDisplayItem {
  return item as ClipDisplayItem
}

// Heading and divider styles
const injectedResolvedFont = inject<ComputedRef<string>>('resolvedFontFamily', computed(() => ''))
const headingStyle = computed(() => ({
  fontSize: `calc(1rem * ${props.textSettings.fontScale})`,
  fontWeight: props.textSettings.fontWeight,
  fontFamily: `'${injectedResolvedFont.value || props.textSettings.fontFamily}', sans-serif`,
  color: `color-mix(in srgb, ${props.textSettings.color} 70%, transparent)`,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.1em'
}))

// Use parent-provided separator style, fallback to default gradient
const injectedSeparatorStyle = inject<Ref<Record<string, string> | null>>('separatorStyle', ref({ background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.06), transparent)' }))
const dividerStyle = computed(() => injectedSeparatorStyle.value ?? { background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.06), transparent)' })

// Section title visibility
const inCard = inject<Ref<boolean>>('sectionInCard', ref(false))
const showTitles = computed(() => props.sectionSettings.showTitles !== false)

// Custom section titles
const authStore = useAuthStore()
const { requirePlan } = usePlanGate()
const sectionTitlesEnabled = computed(() => authStore.canAccessBoolean('customSectionTitles'))
const sectionTitle = computed(() => props.sectionSettings.titles?.clips || DEFAULT_SECTION_TITLES.clips)
const updateSectionTitle = inject<(key: string, value: string) => void>('updateSectionTitle', () => {})

function trackClipView(clip: ClipDisplayItem) {
  if (props.characterId && !props.editMode) {
    $fetch(`/api/character/${props.characterId}/clip/${clip.id}/view`, {
      method: 'POST',
      body: { sessionId: useStatisticsSession() }
    }).catch(() => {})
  }
}

async function openClipPlayer(clip: ClipDisplayItem) {
  if (!clip.url) return

  trackClipView(clip)

  const ClipPlayerModal = defineAsyncComponent(() => import('~/components/modals/character/ClipPlayerModal.vue'))
  const modal = overlay.create(ClipPlayerModal)

  await modal.open({
    clipUrl: clip.url,
    title: clip.title
  }).result
}
</script>

<template>
  <div
    v-if="clips.length > 0 || editMode"
    class="px-2"
    :class="{ 'mt-6': showTitles || !inCard }"
  >
    <!-- Section heading with divider -->
    <div
      v-if="showTitles || !inCard"
      class="flex items-center gap-3 mb-4"
    >
      <div
        class="flex-1 h-px"
        :style="dividerStyle"
      />
      <!-- Edit mode + Pro+ → click to edit -->
      <InlineEditText
        v-if="showTitles && editMode && sectionTitlesEnabled"
        :model-value="sectionSettings.titles?.clips ?? ''"
        :editable="true"
        tag="h3"
        :placeholder="DEFAULT_SECTION_TITLES.clips"
        :display-style="headingStyle"
        class="shrink-0"
        @update:model-value="updateSectionTitle('clips', $event)"
      />
      <!-- Edit mode + Free → locked with Pro badge -->
      <div
        v-else-if="showTitles && editMode"
        class="shrink-0 inline-flex items-center gap-1.5 cursor-pointer rounded-lg transition-colors hover:bg-white/5 px-2 py-0.5"
        @click="requirePlan('Custom Section Titles', 'pro')"
      >
        <h3
          class="text-xs"
          :style="headingStyle"
        >
          {{ DEFAULT_SECTION_TITLES.clips }}
        </h3>
        <UBadge
          color="fuchsia"
          variant="solid"
          size="xs"
        >
          <UIcon
            name="i-lucide-pencil"
            class="size-2.5"
          />
        </UBadge>
      </div>
      <!-- Public view → static text -->
      <h3
        v-else-if="showTitles"
        class="text-xs shrink-0"
        :style="headingStyle"
      >
        {{ sectionTitle }}
      </h3>
      <div
        v-if="showTitles"
        class="flex-1 h-px"
        :style="dividerStyle"
      />
    </div>

    <!-- Single clip (no carousel needed) -->
    <button
      v-if="clips.length === 1"
      class="w-full aspect-video rounded-lg overflow-hidden relative group cursor-pointer border border-white/8 hover:border-white/20 transition-all duration-200"
      @click="openClipPlayer(clips[0]!)"
    >
      <video
        v-if="clips[0]!.url"
        :src="clips[0]!.url"
        preload="metadata"
        muted
        class="w-full h-full object-cover"
      />
      <div class="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <UIcon
          name="i-lucide-play"
          class="size-8 text-white"
        />
      </div>
      <div
        v-if="clips[0]!.title"
        class="absolute bottom-0 left-0 right-0 px-2 py-1 bg-gradient-to-t from-black/70 to-transparent"
      >
        <p class="text-xs text-white/90 truncate">
          {{ clips[0]!.title }}
        </p>
      </div>
    </button>

    <!-- 2 clips: side by side, no carousel -->
    <div
      v-else-if="clips.length === 2"
      class="grid grid-cols-2 gap-2"
    >
      <button
        v-for="clip in clips"
        :key="clip.id"
        class="aspect-video rounded-lg overflow-hidden relative group cursor-pointer border border-white/8 hover:border-white/20 transition-all duration-200"
        @click="openClipPlayer(clip)"
      >
        <video
          v-if="clip.url"
          :src="clip.url"
          preload="metadata"
          muted
          class="w-full h-full object-cover"
        />
        <div class="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <UIcon
            name="i-lucide-play"
            class="size-6 text-white"
          />
        </div>
        <div
          v-if="clip.title"
          class="absolute bottom-0 left-0 right-0 px-1.5 py-0.5 bg-gradient-to-t from-black/70 to-transparent"
        >
          <p class="text-[10px] text-white/90 truncate">
            {{ clip.title }}
          </p>
        </div>
      </button>
    </div>

    <!-- 3+ clips: carousel showing 2 at a time -->
    <div
      v-if="clips.length > 2"
      class="overflow-hidden"
    >
      <UCarousel
        ref="carousel"
        v-slot="{ item }"
        :items="carouselItems"
        arrows
        :prev="{ onClick: onClickPrev }"
        :next="{ onClick: onClickNext }"
        @select="onSelect"
      >
        <button
          class="aspect-video rounded-lg overflow-hidden relative group cursor-pointer border border-white/8 hover:border-white/20 transition-all duration-200"
          @click="openClipPlayer(asClip(item))"
        >
          <video
            v-if="asClip(item).url"
            :src="asClip(item).url!"
            preload="metadata"
            muted
            class="w-full h-full object-cover"
          />
          <div class="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <UIcon
              name="i-lucide-play"
              class="size-6 text-white"
            />
          </div>
          <div
            v-if="asClip(item).title"
            class="absolute bottom-0 left-0 right-0 px-1.5 py-0.5 bg-gradient-to-t from-black/70 to-transparent"
          >
            <p class="text-[10px] text-white/90 truncate">
              {{ asClip(item).title }}
            </p>
          </div>
        </button>
      </UCarousel>

      <!-- Page counter -->
      <p
        v-if="totalPages > 1"
        class="text-center pt-2 text-[10px] tracking-wide"
        :style="{ color: `color-mix(in srgb, ${textSettings.color} 25%, transparent)` }"
      >
        {{ currentPage }} / {{ totalPages }}
      </p>
    </div>

    <!-- Manage button (edit mode) -->
    <button
      v-if="editMode"
      class="mt-3 w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg border border-dashed transition-all duration-200 hover:scale-105 hover:border-white/15"
      :style="{
        borderColor: 'rgba(255, 255, 255, 0.08)',
        color: 'rgba(255, 255, 255, 0.4)'
      }"
      @click="emit('open-clips-section')"
    >
      <UIcon
        name="i-lucide-settings"
        class="size-4"
      />
      <span class="text-sm">Manage clips</span>
    </button>
  </div>
</template>
